import { fromUnixTime, format } from 'date-fns'
import { v4 as uuidv4 } from 'uuid'

import { SERVER_GRAPHQL } from '../constants'
import { getPersistedAuthData } from '../services/auth'

export const generateSafeLink = (pathname) => {
  return encodeURI(pathname)
}

/**
 * Generate the text version of the given `rank`
 *
 * @param {Number} rank - Rank
 * @returns Text version of the given `rank`
 */
export const generateUserRankText = (rank) => {
  switch (rank) {
    case 1:
      return '1st'
    case 2:
      return '2nd'
    case 3:
      return '3rd'
    default:
      return ''
  }
}

/**
 * Sort two strings from A to Z.
 * @param {String} a - First string
 * @param {String} b - Second string
 */
export const sortStringFunc = (a = '', b = '') =>
  a.localeCompare(b, undefined, {
    numeric: true,
    sensitivity: 'base',
  })

/**
 * Sort two numbers.
 * @param {String} a - First string
 * @param {String} b - Second string
 */
export const sortNumberFunc = (a = '', b = '') => a - b

/**
 * Promised-based delay function
 *
 * @param {number} ms - Delay in milliseconds
 * @returns
 */
export const delay = async (ms = 1000) => {
  return new Promise((resolve) =>
    setTimeout(function () {
      resolve()
    }, ms)
  )
}

/**
 * Download profile picture from the server.
 *
 * @param {string} fileName - Profile picture file name
 * @returns Profile picture blob URL
 */
export async function getProfilePic(fileName = '') {
  // Return if we don't know the file name
  if (fileName === '') {
    return
  }

  try {
    const authToken = getPersistedAuthData()?.authToken
    if (!authToken) {
      return null
    }

    const response = await fetch(SERVER_GRAPHQL, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        Authorization: `Bearer ${authToken}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        query: `
          mutation ProfilePhotoUrl($input: ProfilePhotoUrlInput!) {
            profilePhotoUrl(input: $input) {
              url
            }
          }
        `,
        variables: {
          input: {
            clientMutationId: uuidv4(),
            action: 'download',
            fileName,
          },
        },
      }),
    })

    if (!response.ok) {
      return null
    }

    const responseData = await response.json()
    const downloadURL = responseData?.data?.profilePhotoUrl?.url
    if (!downloadURL) {
      return null
    }

    // Download the picture
    const imageBlob = await fetch(downloadURL, {
      method: 'GET',
    }).then((res) => res.blob())
    const profilePicURL = URL.createObjectURL(imageBlob)
    return profilePicURL
  } catch (_) {
    return null
  }
}

/**
 * Function returns the content as a CSV string
 * See https://stackoverflow.com/a/20623188/64904
 * Parameter content:
 *   [
 *.     [header1, header2],
 *.     [data1, data2]
 *.     ...
 *.  ]
 * NB Does not support Date items
 */
export function makeCSV(content) {
  let csv = ''
  content.forEach((value) => {
    value.forEach((item, i) => {
      let innerValue = item === null ? '' : item.toString()
      let result = innerValue.replace(/"/g, '""')
      if (result.search(/("|,|\n)/g) >= 0) {
        result = '"' + result + '"'
      }
      if (i > 0) {
        csv += ','
      }
      csv += result
    })
    csv += '\n'
  })
  return csv
}

/**
 * Generate points report.
 *
 * @param {*} users
 * @param {*} agendaData
 */
export function generatePointsReport(users, pointsUpdateTransactionData) {
  let contents = []
  const headers = [
    'Date Time',
    'Facilitator Name',
    'Facilitator Email',
    'Recipient Name',
    'Recipient Email',
    'Points Awarded',
  ]

  const participants = users.filter((user) =>
    user.capabilities.includes('participant')
  )
  const facilitators = users.filter((user) =>
    user.capabilities.includes('facilitator')
  )

  const rows = pointsUpdateTransactionData.map((row) => {
    const dateTime = fromUnixTime(row.timestamp)
    const facilitator = facilitators.find((f) => f.databaseId === row.updaterId)
    const facilitatorName = facilitator
      ? `${facilitator.firstName} ${facilitator.lastName}`.trim()
      : ''
    const facilitatorEmail = facilitator ? facilitator.email : ''
    const recipient = participants.find((p) => p.databaseId === row.userId)
    const recipientName = recipient
      ? `${recipient.firstName} ${recipient.lastName}`.trim()
      : ''
    const recipientEmail = recipient ? recipient.email : ''

    return [
      format(dateTime, 'yyyy-LL-dd HH:mm:ss'),
      facilitatorName,
      facilitatorEmail,
      recipientName,
      recipientEmail,
      row?.bonus ? `${row?.operation === 'sub' ? '-' : ''}${row?.bonus}` : '',
    ]
  })

  contents = [headers].concat(rows)

  return contents
}

/**
 * Generate full report.
 *
 * @param {*} users
 * @param {*} agendaItems
 * @param {*} learningLoungeItems
 */
export function generateReport(users, agendaItems, learningLoungeItems) {
  let contents = []
  const allAgendaActivities = agendaItems.map(
    ({
      postId,
      title,
      detail: {
        knowledgeCheck: { questions },
      },
    }) => {
      return {
        postId,
        title,
        points: questions
          ? questions.reduce((prev, cur) => prev + cur.point, 0)
          : 0,
      }
    }
  )
  const allLearningLoungeActivities = learningLoungeItems.map(
    ({ postId, title, detail: { questions } }) => {
      return {
        postId,
        title,
        points: questions
          ? questions.reduce((prev, cur) => prev + cur.point, 0)
          : 0,
      }
    }
  )
  const headers = ['Name', 'Email', 'Total Points'].concat(
    allAgendaActivities.map((activity) => activity.title),
    allLearningLoungeActivities.map((activity) => activity.title),
    'RM Point'
  )

  const participants = users.filter((user) =>
    user.capabilities.includes('participant')
  )

  const participantsCompletion = participants.map((p) => {
    const completions = p.completion.map((c) => JSON.parse(c))
    const rmPoints = p.transactions
      ? p.transactions.reduce(
          (prev, cur) =>
            cur.operation === 'add' ? prev + cur.bonus : prev - cur.bonus,
          0
        )
      : 0
    const totalPoints =
      p.completion
        .map((c) => JSON.parse(c).e)
        .reduce((prev, cur) => prev + cur, 0) + rmPoints
    const basicInfo = [`${p.firstName} ${p.lastName}`, p.email, totalPoints]
    const allAgendaActivitiesPoints = allAgendaActivities.map((activity) => {
      const myCompletions = completions.filter(
        (element) => element.p === activity.postId
      )

      if (myCompletions.length > 0) {
        // If found, return the total earned point
        return myCompletions.reduce((prev, cur) => prev + cur.e, 0)
      }

      // Otherwise, return empty string for the column
      return ''
    })
    const allLearningLoungeActivitiesPoints = allLearningLoungeActivities.map(
      (activity) => {
        const myCompletions = completions.filter(
          (element) => element.p === activity.postId
        )

        if (myCompletions.length > 0) {
          // If found, return the total earned point
          return myCompletions.reduce((prev, cur) => prev + cur.e, 0)
        }

        // Otherwise, return empty string for the column
        return ''
      }
    )

    return basicInfo.concat(
      allAgendaActivitiesPoints,
      allLearningLoungeActivitiesPoints,
      rmPoints > 0 ? `${rmPoints}` : ''
    )
  })

  contents = [].concat([headers], participantsCompletion)

  return contents
}

///////////////////////////////////////////////////////////////////////////////
/////////////////////////// Password strength check ///////////////////////////
///////////////////////////////////////////////////////////////////////////////
function checkPasswordStrength(checks = []) {
  const validChecks = checks.filter((check) => check.isValid)

  if (validChecks.length >= checks.length) {
    return {
      strength: 'strong',
      message: `<span class="font-bold text-green">✔&nbsp;&nbsp;&nbsp;Password strength:</span> <span class="font-bold text-green">strong</span>`,
    }
  } else if (
    validChecks.length >= checks.length / 2 &&
    validChecks.length < checks.length
  ) {
    return {
      strength: 'medium',
      message: `<span>✕&nbsp;&nbsp;&nbsp;Password strength:</span> <span class="font-bold text-red-500">medium</span>`,
    }
  } else {
    return {
      strength: 'weak',
      message: `<span>✕&nbsp;&nbsp;&nbsp;Password strength:</span> <span class="font-bold text-red-500">weak</span>`,
    }
  }
}

function checkPasswordAtLeast8Characters(value) {
  return value.length >= 8
    ? {
        isValid: true,
        message: `<span class="text-green">✔&nbsp;&nbsp;&nbsp;At least 8 characters</span>`,
      }
    : {
        isValid: false,
        message: `<span>✕&nbsp;&nbsp;&nbsp;At least 8 characters</span>`,
      }
}

function checkContainNumber(value) {
  return value.match(/\d/g)
    ? {
        isValid: true,
        message: `<span class="text-green">✔&nbsp;&nbsp;&nbsp;Contains a number</span>`,
      }
    : {
        isValid: false,
        message: `<span>✕&nbsp;&nbsp;&nbsp;Contains a number</span>`,
      }
}

function checkContainSymbol(value) {
  return value.match(/[ !"#$%&'()*+,-./:;<=>?@[\]^_`{|}~]/g)
    ? {
        isValid: true,
        message: `<span class="text-green">✔&nbsp;&nbsp;&nbsp;Contains a symbol</span>`,
      }
    : {
        isValid: false,
        message: `<span>✕&nbsp;&nbsp;&nbsp;Contains a symbol</span>`,
      }
}

function checkContainLowercase(value) {
  return value.match(/[a-z]/g)
    ? {
        isValid: true,
        message: `<span class="text-green">✔&nbsp;&nbsp;&nbsp;Contains lowercase letters</span>`,
      }
    : {
        isValid: false,
        message: `<span>✕&nbsp;&nbsp;&nbsp;Contains lowercase letters</span>`,
      }
}

function checkContainUppercase(value) {
  return value.match(/[A-Z]/g)
    ? {
        isValid: true,
        message: `<span class="text-green">✔&nbsp;&nbsp;&nbsp;Contains uppercase letters</span>`,
      }
    : {
        isValid: false,
        message: `<span>✕&nbsp;&nbsp;&nbsp;Contains uppercase letters</span>`,
      }
}

function checkCantStartWithSpace(value) {
  return value.length > 0 && value[0] !== ' ' && value[value.length - 1] !== ' '
    ? {
        isValid: true,
        message: `<span class="text-green">✔&nbsp;&nbsp;&nbsp;Can't start or end with a space</span>`,
      }
    : {
        isValid: false,
        message: `<span>✕&nbsp;&nbsp;&nbsp;Can't start or end with a space</span>`,
      }
}

/**
 * Check password strength
 *
 * @param {string} value - Password value
 * @returns An object of strength indicator and message
 */
export function getStrengthCheckMessage(value) {
  const atLeast8CharactersCheck = checkPasswordAtLeast8Characters(value)
  const containNumberCheck = checkContainNumber(value)
  const containSymbolCheck = checkContainSymbol(value)
  const containLowercaseCheck = checkContainLowercase(value)
  const containUpperCaseCheck = checkContainUppercase(value)
  const cantStartWithSpaceCheck = checkCantStartWithSpace(value)
  const strengthCheck = checkPasswordStrength([
    atLeast8CharactersCheck,
    containNumberCheck,
    containSymbolCheck,
    containLowercaseCheck,
    containUpperCaseCheck,
    cantStartWithSpaceCheck,
  ])

  return strengthCheck.strength === 'strong'
    ? { strength: strengthCheck.strength, message: strengthCheck.message }
    : {
        strength: strengthCheck.strength,
        message: strengthCheck.message.concat(
          '<br/>',
          atLeast8CharactersCheck.message,
          '<br/>',
          containNumberCheck.message,
          '<br/>',
          containSymbolCheck.message,
          '<br/>',
          containLowercaseCheck.message,
          '<br/>',
          containUpperCaseCheck.message,
          '<br/>',
          cantStartWithSpaceCheck.message
        ),
      }
}

/**
 * Recursively remove __typename from apollo client obj data
 *
 * @param {*} obj Can either be an array or an object
 * @returns obj without __typename
 */
export const removeTypeName = (obj) => {
  if (Array.isArray(obj)) {
    return obj.map((o) => removeTypeName(o))
  } else if (typeof obj === 'object' && obj) {
    const { __typename, ...rest } = obj
    let cleanObj = {}

    for (const [key, value] of Object.entries(rest)) {
      cleanObj[key] = removeTypeName(value)
    }

    return cleanObj
  }

  return obj
}

export const applyUpdatedData = (
  updatedData = [],
  page = '',
  change = {},
  originalData
) => {
  if (page === 'HOME_PAGE') {
    if (typeof change.index === 'number' && change.subsections) {
      if (updatedData.length === 0) {
        updatedData = removeTypeName(change.subsections)
      }

      if (change.type === 'section2SubsectionTitle') {
        // Section 2 > Subsection title
        updatedData = updatedData.map((subsection, subsectionIndex) =>
          change.index === subsectionIndex
            ? { ...subsection, title: change.content }
            : subsection
        )
      }

      if (change.type === 'section2SubsectionContent') {
        // Section 2 > Subsection content
        updatedData = updatedData.map((subsection, subsectionIndex) =>
          change.index === subsectionIndex
            ? { ...subsection, content: change.content }
            : subsection
        )
      }
    }
  } else if (page === 'AGENDA_ITEM') {
    if (updatedData.length === 0 && Array.isArray(originalData)) {
      updatedData = removeTypeName(originalData)
    }

    // Agenda item > detail > knowledgeCheck > question > statement (update)
    if (change.type === 'questionStatement') {
      updatedData = updatedData.map((question) =>
        change.id === question.id
          ? { ...question, statement: change.content }
          : question
      )
    }
    // Agenda item > detail > modules (create, remove)
    else if (
      ['detailModules', 'detailKnowledgeCheckQuestions'].includes(change.type)
    ) {
      // Update action
      if (change.action === 'update') {
        // Current usecase is: knowledge check update
        if (Array.isArray(change.data)) {
          updatedData = removeTypeName(Array.from(change.data))
        } else {
          // Agenda item > detail > module (update)
          updatedData = updatedData.map((module) =>
            change.id === module.id ? removeTypeName(change.data) : module
          )
        }
      }
      // Add new module action
      else if (
        change.action === 'create' &&
        !isNaN(parseInt(change.atIndex)) &&
        change.data
      ) {
        updatedData.splice(change.atIndex, 0, removeTypeName(change.data))
      }
      // Remove action
      else if (change.action === 'remove' && !isNaN(parseInt(change.atIndex))) {
        updatedData.splice(change.atIndex, 1)
      }
      // Reorder action
      else if (change.action === 'reorder' && change.data) {
        updatedData = removeTypeName(Array.from(change.data))
      }
    }
  } else if (page === 'LEARNING_LOUNGE_ITEM') {
    if (updatedData.length === 0 && Array.isArray(originalData)) {
      updatedData = removeTypeName(originalData)
    }

    if (['detailKnowledgeCheckQuestions'].includes(change.type)) {
      // Add new module action
      if (
        change.action === 'create' &&
        !isNaN(parseInt(change.atIndex)) &&
        change.data
      ) {
        if (!updatedData.find((data) => data.id === change.data?.id)) {
          updatedData.splice(change.atIndex, 0, removeTypeName(change.data))
        }
      }
      // Remove action
      else if (change.action === 'remove' && !isNaN(parseInt(change.atIndex))) {
        updatedData.splice(change.atIndex, 1)
      }
      // Reorder action
      else if (change.action === 'reorder' && change.data) {
        updatedData = removeTypeName(Array.from(change.data))
      }
      // Update action
      // Current usecase is: knowledge check update
      else if (change.action === 'update' && Array.isArray(change.data)) {
        updatedData = removeTypeName(Array.from(change.data))
      }
    }
  }

  return updatedData
}

/**
 * Generate necessary mutation variables for page and page item
 *
 * @param {*} editStatus
 * @param {*} change
 * @returns Tuple of page variables and item variables
 */
export const generateMutationVariables = (editStatus = {}) => {
  let updatedSubsections = []
  let updatedModules = []
  let updatedQuestions = []
  let pageVariables = {}
  let itemVariables = []

  // Stop if we don't have proper data
  if (!editStatus || !Array.isArray(editStatus.changes)) {
    return [pageVariables, itemVariables]
  }

  let { originalData } = editStatus
  for (let i = 0; i < editStatus.changes.length; i++) {
    const change = editStatus.changes[i]
    const page = change.page
    const type = change.type
    const action = change.action

    // All removed posts share the same variable pattern
    // The only field needed in `variables` is `id`
    // `action` field will be removed in the publish function before executing
    // the delete mutation
    if (
      !['detailModules', 'detailKnowledgeCheckQuestions'].includes(type) &&
      action === 'remove'
    ) {
      itemVariables.push({
        page,
        action,
        variables: {
          id: change.databaseId,
        },
      })
      continue
    }

    if (page === 'HOME_PAGE') {
      /**
       * HOME_PAGE
       */

      if (
        !pageVariables.variables ||
        typeof pageVariables.variables.id !== 'number'
      ) {
        pageVariables = {
          page,
          action,
          variables: {
            id: change.databaseId,
          },
        }
      }

      const { variables } = pageVariables

      if (typeof change.index === 'number' && change.subsections) {
        updatedSubsections = applyUpdatedData(updatedSubsections, page, change)

        pageVariables.variables = {
          ...variables,
          section2: {
            ...variables.section2,
            subsections: updatedSubsections,
          },
        }
      } else {
        // Home page > Hero subtitle
        if (type === 'heroSubtitle') {
          pageVariables.variables = {
            ...variables,
            subtitle: change.content,
          }
        }
        // Home page > Hero title
        else if (type === 'heroTitle') {
          pageVariables.variables = {
            ...variables,
            title: change.content,
          }
        }
        // Home page > Hero overview
        else if (type === 'heroOverview') {
          pageVariables.variables = {
            ...variables,
            overview: change.content,
          }
        }
        // Home page > Section 1 title
        else if (type === 'section1Title') {
          pageVariables.variables = {
            ...variables,
            section1: { ...variables.section1, title: change.content },
          }
        }
        // Home page > Section 1 content
        else if (type === 'section1Content') {
          pageVariables.variables = {
            ...variables,
            section1: { ...variables.section1, content: change.content },
          }
        }
        // Home page > Section 2 title
        else if (type === 'section2Title') {
          pageVariables.variables = {
            ...variables,
            section2: {
              ...variables.section2,
              title: change.content,
            },
          }
        }
      }
    } else if (page === 'AGENDA_PAGE') {
      /**
       * AGENDA_PAGE
       */

      if (
        !pageVariables.variables ||
        typeof pageVariables.variables.id !== 'number'
      ) {
        pageVariables = {
          page,
          action,
          variables: {
            id: change.databaseId,
          },
        }
      }

      const { variables } = pageVariables

      // Agenda page title
      if (type === 'title') {
        pageVariables.variables = {
          ...variables,
          title: change.content,
        }
      }
      // Agenda page overview
      else if (type === 'overview') {
        pageVariables.variables = {
          ...variables,
          overview: change.content,
        }
      }
      // Agenda page instruction
      else if (type === 'instruction') {
        pageVariables.variables = {
          ...variables,
          instruction: change.content,
        }
      }
    } else if (page === 'AGENDA_ITEM') {
      /**
       * AGENDA_ITEM
       */

      // This is a change in detail > modules
      if (['detailModules', 'module'].includes(type)) {
        const variableIndex = itemVariables.findIndex(
          (itemVariable) => itemVariable.variables.id === change.databaseId
        )
        updatedModules = applyUpdatedData(
          updatedModules,
          page,
          change,
          originalData?.modules ?? []
        )

        // Update the originalData variable
        if (updatedModules.length > 0) {
          originalData.modules = [...updatedModules]
        }

        if (variableIndex > -1) {
          // Update existing change
          const variables = itemVariables[variableIndex].variables

          itemVariables[variableIndex].variables = {
            ...variables,
            detail: {
              ...variables.detail,
              modules: updatedModules,
            },
          }
        } else {
          // This change is new
          itemVariables.push({
            page,
            // `create` or `delete` action in modules is just an `update`
            // action
            action: 'update',
            variables: {
              id: change.databaseId,
              detail: { modules: updatedModules },
            },
          })
        }
      }
      // This is a change in detail > knowledgeCheck > questions
      else if (
        ['questionStatement', 'detailKnowledgeCheckQuestions'].includes(type)
      ) {
        const variableIndex = itemVariables.findIndex(
          (itemVariable) => itemVariable.variables.id === change.databaseId
        )
        updatedQuestions = applyUpdatedData(
          updatedQuestions,
          page,
          change,
          originalData?.questions ?? []
        )

        if (variableIndex > -1) {
          // Update existing change
          const variables = itemVariables[variableIndex].variables

          itemVariables[variableIndex].variables = {
            ...variables,
            detail: {
              ...variables.detail,
              knowledgeCheck: {
                ...variables.detail?.knowledgeCheck,
                questions: updatedQuestions,
              },
            },
          }
        } else {
          // This change is new
          itemVariables.push({
            page,
            action: 'update',
            variables: {
              id: change.databaseId,
              detail: {
                knowledgeCheck: { questions: updatedQuestions },
              },
            },
          })
        }
      }
    } else if (page === 'TIPS_TOOLS_PAGE') {
      /**
       * TIPS_TOOLS_PAGE
       */
      if (
        !pageVariables.variables ||
        typeof pageVariables.variables.id !== 'number'
      ) {
        pageVariables = {
          page,
          action,
          variables: {
            id: change.databaseId,
          },
        }
      }

      const { variables } = pageVariables

      // Tips & tools page title
      if (type === 'title') {
        pageVariables.variables = {
          ...variables,
          title: change.content,
        }
      }
      // Tips & tools page overview
      else if (type === 'overview') {
        pageVariables.variables = {
          ...variables,
          overview: change.content,
        }
      }
    } else if (page === 'TIPS_TOOLS_ITEM') {
      const variableIndex = itemVariables.findIndex(
        (itemVariable) => itemVariable.variables.id === change.databaseId
      )

      if (variableIndex > -1) {
        // Update existing change
        const variables = itemVariables[variableIndex].variables

        // Tips & tools item title
        if (type === 'title') {
          itemVariables[variableIndex].variables = {
            ...variables,
            title: change.content,
          }
        }
        // Tips & tools item order
        else if (type === 'order') {
          itemVariables[variableIndex].variables = {
            ...variables,
            order: change.content,
          }
        }
        // Tips & tools item postId
        else if (type === 'postId') {
          itemVariables[variableIndex].variables = {
            ...variables,
            postId: change.content,
          }
        }
        // Tips & tools item detail
        else if (type === 'detail') {
          itemVariables[variableIndex].variables = {
            ...variables,
            detail: change.content,
          }
        }

        // Tips & tools item's entire post
        if (type === 'post') {
          itemVariables[variableIndex].variables = {
            ...variables,
            ...removeTypeName(change.content),
          }
        }
      } else {
        let input = {}
        if (type === 'title') {
          input = { title: change.content }
        } else if (type === 'order') {
          input = { order: change.content }
        } else if (type === 'post') {
          input = removeTypeName(change.content)
        }
        itemVariables.push({
          page,
          action,
          variables: {
            id: change.databaseId,
            ...input,
          },
        })
      }
    } else if (page === 'LEARNING_LOUNGE_PAGE') {
      // LEARNING_LOUNGE_PAGE > item list
      if (type === 'item' && action === 'create') {
        itemVariables.push({
          page: 'LEARNING_LOUNGE_ITEM',
          action,
          variables: {
            clientMutationId: uuidv4(),
            ...change.content,
          },
        })
      } else if (type.startsWith('item')) {
        const variableIndex = itemVariables.findIndex(
          (itemVariable) => itemVariable.variables.id === change.databaseId
        )

        if (variableIndex > -1) {
          // Update existing change
          const variables = itemVariables[variableIndex].variables

          // Learning lounge item title
          if (type === 'itemTitle') {
            itemVariables[variableIndex].variables = {
              ...variables,
              title: change.content,
            }
          }
          // Learning lounge item order
          else if (type === 'itemOrder') {
            itemVariables[variableIndex].variables = {
              ...variables,
              order: change.content,
            }
          }
          // Learning lounge item duration
          else if (type === 'itemDuration') {
            itemVariables[variableIndex].variables = {
              ...variables,
              duration: change.content,
            }
          }
          // Learning lounge item thumbnail
          else if (type === 'itemThumbnail') {
            itemVariables[variableIndex].variables = {
              ...variables,
              thumbnail: change.content,
            }
          }
          // Learning lounge item required
          else if (type === 'itemRequired') {
            itemVariables[variableIndex].variables = {
              ...variables,
              required: change.content,
            }
          }
        } else {
          // This change is new
          let input = {}

          if (type === 'itemTitle') {
            input = { title: change.content }
          } else if (type === 'itemOrder') {
            input = { order: change.content }
          } else if (type === 'itemDuration') {
            input = { duration: change.content }
          } else if (type === 'itemThumbnail') {
            input = { thumbnail: change.content }
          } else if (type === 'itemRequired') {
            input = { required: change.content }
          }

          itemVariables.push({
            page: 'LEARNING_LOUNGE_ITEM',
            action: 'update',
            variables: {
              id: change.databaseId,
              ...input,
            },
          })
        }
      }

      // LEARNING_LOUNGE_PAGE > page data
      else {
        if (
          !pageVariables.variables ||
          typeof pageVariables.variables.id !== 'number'
        ) {
          pageVariables = {
            page,
            action,
            variables: {
              id: change.databaseId,
            },
          }
        }

        const { variables } = pageVariables

        // Learning lounge page title
        if (type === 'title') {
          pageVariables.variables = {
            ...variables,
            title: change.content,
          }
        }
        // Learning lounge page overview
        else if (type === 'overview') {
          pageVariables.variables = {
            ...variables,
            overview: change.content,
          }
        }
      }
    } else if (page === 'LEARNING_LOUNGE_ITEM') {
      /**
       * LEARNING_LOUNGE_ITEM
       */
      const variableIndex = itemVariables.findIndex(
        (itemVariable) => itemVariable.variables.id === change.databaseId
      )

      if (variableIndex > -1) {
        // Update existing change
        const variables = itemVariables[variableIndex].variables

        // Learning lounge item title
        if (type === 'title') {
          itemVariables[variableIndex].variables = {
            ...variables,
            title: change.content,
          }
        }
        // Learning lounge item order
        else if (type === 'order') {
          itemVariables[variableIndex].variables = {
            ...variables,
            order: change.content,
          }
        }
        // Learning lounge item overview
        else if (type === 'overview') {
          itemVariables[variableIndex].variables = {
            ...variables,
            detail: { overview: change.content },
          }
        }
        // Learning lounge item steps
        else if (type === 'steps') {
          itemVariables[variableIndex].variables = {
            ...variables,
            detail: {
              ...variables.detail,
              steps: change.content
                // Remove <li>
                ?.replace(/<li>/g, '')
                // Replace </li> with ||| for separation
                .replace(/<\/li>/g, '|||')
                // Split list into array of strings
                .split('|||')
                // Remove the last empty string
                .slice(0, -1),
            },
          }
        }
        // Learning lounge item note
        else if (type === 'note') {
          itemVariables[variableIndex].variables = {
            ...variables,
            detail: {
              ...variables.detail,
              note: change.content,
            },
          }
        }
        // Learning lounge item resources
        else if (type === 'resources') {
          itemVariables[variableIndex].variables = {
            ...variables,
            detail: {
              ...variables.detail,
              resources: removeTypeName(change.content),
            },
          }
        }
        // Learning lounge item questions
        else if (
          ['questionStatement', 'detailKnowledgeCheckQuestions'].includes(type)
        ) {
          const variableIndex = itemVariables.findIndex(
            (itemVariable) => itemVariable.variables.id === change.databaseId
          )
          updatedQuestions = applyUpdatedData(
            updatedQuestions,
            page,
            change,
            originalData?.questions ?? []
          )

          if (variableIndex > -1) {
            // Update existing change
            const variables = itemVariables[variableIndex].variables

            itemVariables[variableIndex].variables = {
              ...variables,
              detail: {
                ...variables.detail,
                knowledgeCheck: {
                  ...variables.detail?.knowledgeCheck,
                  questions: updatedQuestions,
                },
              },
            }
          }
        }
      } else {
        // This change is new
        let input = {}

        if (type === 'title') {
          input = { title: change.content }
        } else if (type === 'order') {
          input = { order: change.content }
        } else if (type === 'overview') {
          input = { detail: { overview: change.content } }
        } else if (type === 'steps') {
          input = {
            detail: {
              steps: change.content
                // Remove <ol>
                ?.replace(/<ol>/g, '')
                // Remove </ol>
                ?.replace(/<\/ol>/g, '')
                // Remove <li>
                ?.replace(/<li>/g, '')
                // Replace </li> with ||| for separation
                .replace(/<\/li>/g, '|||')
                // Split list into array of strings
                .split('|||')
                // Remove the last empty string
                .slice(0, -1),
            },
          }
        } else if (type === 'note') {
          input = { detail: { note: change.content } }
        } else if (type === 'resources') {
          input = { detail: { resources: removeTypeName(change.content) } }
        } else if (
          ['questionStatement', 'detailKnowledgeCheckQuestions'].includes(type)
        ) {
          updatedQuestions = applyUpdatedData(
            updatedQuestions,
            page,
            change,
            originalData?.questions ?? []
          )
          input = { detail: { questions: updatedQuestions } }
        }
        itemVariables.push({
          page,
          action: 'update',
          variables: {
            id: change.databaseId,
            ...input,
          },
        })
      }
    }
  }

  return [pageVariables, itemVariables]
}

export const serializeFromQuery = (searchParams) => {
  let curSearchParams = {}
  for (const [key, value] of searchParams) {
    curSearchParams[key] = value
  }

  return curSearchParams
}

/**
 * Normalize completion array to an array of JS object
 *
 * @param {[*]} completions - Completion array
 * @returns
 */
export const normalizeCompletion = (completions = []) => {
  if (!Array.isArray(completions)) {
    return []
  }

  return completions.map((completion) => {
    if (typeof completion === 'string') {
      try {
        return JSON.parse(completion)
      } catch {
        return {}
      }
    } else {
      return completion
    }
  })
}

/**
 * Check whether the user has completed all KC of the post with `postId`
 *
 * @param {[*]} completions - An array of all completions
 * @param {string} postId - Post ID
 * @param {[*]} questions - Questions array
 * @returns
 */
export const checkCompletion = (
  completions = [],
  postId = '',
  questions = []
) => {
  if (typeof postId !== 'string' || postId === '') {
    return false
  }

  const completedQuestions = completions.filter((completion) => {
    try {
      if (completion.p === postId) {
        return true
      }
    } catch {
      return false
    }

    return false
  })

  if (completedQuestions.length === 0) {
    return false
  }

  if (completedQuestions.length !== questions.length) {
    return false
  }

  // When all questions match, the user has successfully completed it.
  if (
    completedQuestions.filter((cq) => questions.find((q) => cq.i === q.id))
      .length === 0
  ) {
    return true
  }

  return false
}
